import {
  createContext,
  useCallback,
  useEffect,
  useState,
} from "react";

/**
 * Homepage marketing wrappers without framer-motion (Lighthouse TBT / main-thread cost).
 * API matches `HomeMotionPrimitives.jsx` so sections can swap imports in one line.
 */
const HomeStripSectionInViewContext = createContext(undefined);

export function HomeReveal({ children, className = "" }) {
  return <div className={className}>{children}</div>;
}

export function HomeSectionTitle({
  children,
  className = "",
  as: Tag = "h2",
  sharpText = false,
}) {
  const Comp = Tag;
  const mergedClassName = ["noto-font", className].filter(Boolean).join(" ");
  if (sharpText) {
    return (
      <Comp className={mergedClassName}>
        <span
          className="block [backface-visibility:hidden]"
          style={{ WebkitFontSmoothing: "antialiased" }}
        >
          {children}
        </span>
      </Comp>
    );
  }
  return <Comp className={mergedClassName}>{children}</Comp>;
}

export function HomeCarouselBtn({ className = "", children, ...props }) {
  return (
    <button type="button" className={className} {...props}>
      {children}
    </button>
  );
}

export function HomeRevealStripItem({
  children,
  className = "",
  viewportRoot = null,
  replayViewport = false,
}) {
  const [syncInView, setSyncInView] = useState(true);
  const [stripObservedEl, setStripObservedEl] = useState(null);
  const setStripRef = useCallback((node) => {
    if (!node) {
      setStripObservedEl(null);
      return;
    }
    setStripObservedEl(node instanceof Element ? node : null);
  }, []);

  useEffect(() => {
    if (!replayViewport) return;
    const el = stripObservedEl;
    if (typeof IntersectionObserver === "undefined") return;
    if (!(el instanceof Element)) return;
    const root = viewportRoot?.current;
    const rootEl = root instanceof Element ? root : null;
    const obs = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (!e) return;
        setSyncInView(e.isIntersecting);
      },
      { root: rootEl, rootMargin: "48px 0px 48px 0px", threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [replayViewport, stripObservedEl, viewportRoot]);

  const inner = replayViewport ? (
    <HomeStripSectionInViewContext.Provider value={syncInView}>
      {children}
    </HomeStripSectionInViewContext.Provider>
  ) : (
    children
  );

  return (
    <div className={className} ref={replayViewport ? setStripRef : undefined}>
      {inner}
    </div>
  );
}
